// R60 — THE CARD LIBRARY THAT DID NOT LOAD SAID THE SEARCH FOUND NOTHING.
//
// THE HUNT is the pointer's, and this is the third route of the fabricated-collection census's last
// four call sites. `TarotLibrary`'s opening read was `r.ok ? r.json() : { cards: [] }` followed by
// `.catch(() => {})` and `.finally(() => setBusy(false))`, so a failed read lands in exactly the
// same place a successful empty one does — and the grid's own empty state then says:
//
//     No card matches that.
//
// **THAT SENTENCE IS ABOUT HER SEARCH BOX.** She typed nothing, all 78 cards are in the database,
// and the room tells her her query matched none of them. It is not a missing error message, it is a
// WRONG one, which is worse than R58's absent section: an absent control has no error state, but a
// confident wrong sentence sends her off to fix something that was never the problem (R15/R16's
// mutation-run lesson, one room over).
//
// **AND THIS ROUTE IS THE ONE THAT ALREADY GOT THE SERVER SIDE RIGHT**, measured 2026-08-01 and
// recorded so nobody re-hunts it: `handleTarotCards` answers **401** with "Sign in to open the card
// library." and **500** with "The card library could not be loaded right now." Both sentences were
// already composed, and neither had ever reached a member — the client threw away the whole payload
// before it could. That is R51's finding (four sentences that reached nobody) on a READ.
//
// So the answer is the FOUR-WAY one and deliberately not R58's `T[] | null`: unlike the config
// menus, this route sits behind sign-in and composes a real refusal, and collapsing a 401 into
// transport noise is R18's distinction — a status the server DELIBERATELY SENT is an ANSWER.
//
// `fetchCardMeanings` comes with it because it is the same library's other read, it was ALREADY
// honest (it reads `d?.ok === false || !d?.meanings`), and its sentence was typed TWICE inside one
// component (R47's count-the-copies). One reader for the library, one place its sentences live.
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law).

export const cardsPath = "/api/tarot/cards";
export const cardPath = (slug: string) => `/api/tarot/card?slug=${encodeURIComponent(slug)}`;

// FOUR answers, and the two in the middle are the ones a status code cannot tell apart. The shape is
// deliberately `src/lib/saved-reports.ts`'s — a second shape for the same four-way question is a
// second answer key (R51's ruling, applied since).
//   sign-in — the session is gone (401). Nothing is lost; she signs in and the library opens.
//   refused — the server DELIBERATELY said no (500 with its own sentence), and it said why.
//   offline — the request never landed. Nothing was decided; transport noise (R18).
export type CardsAnswer<T> =
  | { ok: true; cards: T[] }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

type Json = { ok?: boolean; message?: string; cards?: unknown; meanings?: unknown };

// THE DECK. **A 200 CARRYING NO `cards` ARRAY IS A REFUSAL, NOT AN EMPTY LIBRARY** (R18's
// seal-status lesson, R53's `data` guard): the rows come back in the shape the route returns, so an
// absent array is a malformed answer and there is nothing to draw. `doFetch` is a PARAMETER (R46).
export async function listLibraryCards<T>(doFetch: typeof fetch = fetch): Promise<CardsAnswer<T>> {
  try {
    const res = await doFetch(cardsPath);
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => ({}))) as Json;
    if (res.ok && data?.ok !== false && Array.isArray(data?.cards)) {
      return { ok: true, cards: data.cards as T[] };
    }
    return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// ONE CARD'S PAGE. Already honest before this round and moved here whole, because the sentence it
// falls back to was typed at both of its own failure branches.
export type MeaningsAnswer<T> =
  | { ok: true; meanings: T }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

export async function fetchCardMeanings<T>(slug: string, doFetch: typeof fetch = fetch): Promise<MeaningsAnswer<T>> {
  try {
    const res = await doFetch(cardPath(slug));
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => ({}))) as Json;
    if (res.ok && data?.ok !== false && data?.meanings) return { ok: true, meanings: data.meanings as T };
    return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

export type CardLibraryCopy = { signIn: string; failed: string };

// The sentence, derived from the answer rather than typed per branch. **A SUCCESS SAYS NOTHING** —
// the cards being there is the whole answer. The server's own sentence is preferred where it sent
// one, because it is the only one that knows which of the two things went wrong.
//
// **THE LIBRARY'S COPY IS ENGLISH ON PURPOSE AND THAT IS A RULING, NOT AN OVERSIGHT.** Every other
// sentence on this surface — its title, its search placeholder, its four lenses — is bare English
// (the tarot room's remaining English, on the standing list for her word), and 14-B6's own lesson is
// that a translated line over an untranslated screen is the DEFECT, not the fix. So the copy is a
// PARAMETER (`saved-books.ts`'s fallback shape) and the surface hands in what it speaks.
export function cardLibraryNote<T>(answer: CardsAnswer<T> | MeaningsAnswer<T>, copy: CardLibraryCopy): string {
  if (answer.ok) return "";
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}
